import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'
import { putCache, readCache } from './db'
import { useSyncStore } from './sync'
import { indexPeople, personName } from './people'
import type {
  AuditEntry, Grn, Material, PendingAction, Profile, PurchaseOrder,
  PurchaseRequisition, Site, StockRow, Vendor,
} from './types'

/**
 * Read path.
 *
 * Every query tries the network first and mirrors the result into IndexedDB.
 * When the network is unavailable — or simply too slow to answer — the cached
 * copy is served instead, and the caller is told the data is stale so the UI can
 * say so honestly rather than pretending.
 */

// deno-lint-ignore no-explicit-any
type Any = any

export interface QueryResult<T> {
  data: T[]
  loading: boolean
  error: string | null
  fromCache: boolean
  refresh: () => Promise<void>
}

const NETWORK_TIMEOUT_MS = 12_000 // generous: these sites are on 2G

function withTimeout<T = Any>(p: PromiseLike<T>, ms = NETWORK_TIMEOUT_MS): Promise<T> {
  return Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Network timed out')), ms)),
  ])
}

/**
 * @param collection cache bucket name (usually the table name)
 * @param build      builds the PostgREST query
 * @param cacheable  mirror results locally (false for volatile aggregates)
 */
export function useQuery<T>(
  collection: string,
  build: () => Any,
  deps: unknown[] = [],
  opts: { cacheable?: boolean; idField?: string; enabled?: boolean } = {},
): QueryResult<T> {
  const { cacheable = true, idField = 'id', enabled = true } = opts
  const online = useSyncStore((s) => s.online)
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fromCache, setFromCache] = useState(false)

  const run = useCallback(async () => {
    if (!enabled) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      if (!navigator.onLine) throw new Error('offline')
      const { data: rows, error: qErr } = await withTimeout<{ data: T[] | null; error: { message: string } | null }>(build())
      if (qErr) throw new Error(qErr.message)
      setData((rows ?? []) as T[])
      setFromCache(false)
      if (cacheable && rows?.length) await putCache(collection, rows, idField)
    } catch (e) {
      const cached = await readCache<T>(collection)
      if (cached.length) {
        setData(cached)
        setFromCache(true)
        setError(null)
      } else {
        setData([])
        setFromCache(false)
        const msg = e instanceof Error ? e.message : String(e)
        setError(msg === 'offline'
          ? 'You are offline and this has not been downloaded yet.'
          : msg)
      }
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, enabled, online, ...deps])

  useEffect(() => { void run() }, [run])

  return { data, loading, error, fromCache, refresh: run }
}

// ---------------------------------------------------------------------------
// Master data — pulled in bulk on login so a site device works offline all day
// ---------------------------------------------------------------------------
export async function primeMasterData(): Promise<{ ok: boolean; error?: string }> {
  try {
    const pulls: Array<[string, Any]> = [
      ['sites', supabase.from('sites').select('*').order('site_name')],
      ['materials', supabase.from('materials').select('*').eq('is_active', true).order('item_name')],
      ['vendors', supabase.from('vendors').select('*').eq('is_active', true).order('company_name')],
      ['profiles', supabase.from('profiles').select('*').eq('is_active', true).order('full_name')],
      ['po_terms', supabase.from('po_terms').select('*').eq('is_active', true).order('sort_order')],
      ['warranty_terms', supabase.from('warranty_terms').select('*').eq('is_active', true).order('sort_order')],
      ['payment_terms', supabase.from('payment_terms').select('*').eq('is_active', true).order('sort_order')],
      ['delivery_terms', supabase.from('delivery_terms').select('*').eq('is_active', true).order('sort_order')],
      ['asset_categories', supabase.from('asset_categories').select('*').order('id')],
      ['states', supabase.from('states').select('*').order('state_name')],
      ['company_profile', supabase.from('company_profile').select('*')],
      ['inventory_balances', supabase.from('v_stock_position').select('*')],
    ]
    for (const [name, q] of pulls) {
      const { data, error } = await withTimeout<{ data: Any[] | null; error: { message: string } | null }>(q, 25_000)
      if (error) throw new Error(`${name}: ${error.message}`)
      const idField = name === 'inventory_balances' ? 'material_id'
        : name === 'states' ? 'state_code'
        : name === 'company_profile' ? 'id' : 'id'
      if (data?.length) await putCache(name, data, idField)
    }
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/**
 * WHAT THE DATABASE ACTUALLY SAID.
 *
 * Reported 19-09-2026. Raising the first reverse-charge self-invoice failed
 * and the screen said "Could not raise it". The database had said:
 *
 *     invalid input value for enum audit_action: "RCM_SELF_INVOICE"
 *
 * — a sentence that names the fault exactly. It was thrown away by the
 * handler, which read:
 *
 *     catch (e) { toast.error(e instanceof Error ? e.message : 'Could not …') }
 *
 * Supabase does not throw an Error. `.rpc()` and `.from()` RETURN
 * { data, error }, and that error is a plain object — { message, details,
 * hint, code } — so `e instanceof Error` is false for every database failure
 * there is, and the fallback wins every single time. The one branch that
 * could carry the real message was the branch that never ran.
 *
 * Every refusal in this application is a written sentence, argued over and
 * put there to be read: "Bill RA-3 is DRAFT. A contractor's invoice can only
 * be recorded against a bill that has been certified…". Discarding them for
 * "Could not raise it" wastes the whole of that work and turns a five-second
 * fix into an afternoon.
 *
 * So: pass whatever was caught, and a fallback for the case where there is
 * genuinely nothing to say (a network drop, a thrown string).
 */
export function dbMessage(e: unknown, fallback: string): string {
  if (!e) return fallback
  if (typeof e === 'string') return e.trim() || fallback

  // A PostgrestError, a FunctionsError, or anything else shaped like one.
  const o = e as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
  const message = typeof o.message === 'string' ? o.message.trim() : ''
  if (!message) return fallback

  // details and hint carry the "why" on a constraint or enum failure, and are
  // exactly what is missing when somebody reads a message and still cannot
  // tell what to do. Appended, not substituted.
  const extra = [o.details, o.hint]
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((x) => x.trim())
    .filter((x) => !message.includes(x))

  return extra.length ? `${message} (${extra.join('; ')})` : message
}

export const useSites = () =>
  useQuery<Site>('sites', () => supabase.from('sites').select('*').order('site_name'))

export const useMaterials = () =>
  useQuery<Material>('materials', () =>
    supabase.from('materials').select('*').eq('is_active', true).order('item_name'))

export const useVendors = () =>
  useQuery<Vendor>('vendors', () =>
    supabase.from('vendors').select('*').eq('is_active', true).order('company_name'))

// Ordered by staff code so the list reads as a sequence (USR-001, USR-002, ...).
// nullsFirst: false keeps any code-less row at the bottom rather than the top.
export const useProfiles = () =>
  useQuery<Profile>('profiles', () =>
    supabase.from('profiles').select('*').order('user_code', { ascending: true, nullsFirst: false }))

/**
 * The staff list turned into a name lookup, for every screen that has to draw
 * WHO did something.
 *
 * Deliberately built on useProfiles(), which fetches every row — including
 * people who have left. An audit trail is largely a record of the past, and
 * some of it was done by somebody who has since resigned; filtering to active
 * staff would blank the name on exactly the rows that matter most.
 *
 * NOTE FOR WHOEVER EDITS useProfiles NEXT: adding .eq('is_active', true) there
 * would silently turn resigned people into their role on every trail in the
 * application, and nothing would look broken. See src/lib/people.ts.
 */
export function usePeople() {
  const { data: profiles, loading } = useProfiles()
  const index = useMemo(() => indexPeople(profiles), [profiles])
  return {
    index,
    loading,
    /** The name to draw for one row. Never returns an email address. */
    name: (who: { id?: string | null; email?: string | null; role?: string | null }) =>
      personName(index, who),
  }
}

/**
 * The site list turned into a name lookup, for every screen that has to draw
 * WHERE something happened.
 *
 * Built on 05-09-2026. Document screens were printing the raw key —
 * "SITE-TRP-05" — on requisitions, orders, receipts and issues. An approver
 * deciding whether to buy something has to know it is for Vidyasagar Market,
 * not decode an identifier; at nineteen sites that is friction, and at the
 * hundred-plus the company is heading for it is unworkable.
 *
 * This mirrors usePeople(), which exists for exactly the same reason: a screen
 * should show the thing a human knows, and the lookup should live in one place
 * rather than being repeated at every call site with a slightly different
 * fallback.
 *
 * THE FALLBACK IS THE ID, NEVER A BLANK. If a document points at a site that
 * has since been removed — as SITE-TRP-22 was — the identifier is still shown.
 * A row reading "SITE-TRP-22" is a puzzle somebody can solve; an empty cell
 * looks like a fault in the application.
 */
export function useSiteNames() {
  const { data: sites, loading } = useSites()
  const index = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of sites) if (s.id) m.set(s.id, s.site_name ?? s.id)
    return m
  }, [sites])
  return {
    index,
    loading,
    /** The site name to draw. Falls back to the id, never to nothing. */
    siteName: (id?: string | null) => (id ? (index.get(id) ?? id) : '—'),
  }
}

export const useTerms = (table: 'po_terms' | 'warranty_terms' | 'payment_terms' | 'delivery_terms') =>
  useQuery<Any>(table, () => supabase.from(table).select('*').eq('is_active', true).order('sort_order'))

/**
 * The payment terms a WORKS contract may offer — 0171.
 *
 * The same master the purchase order reads, filtered by works_applicable.
 * Eighteen of the forty-seven are about delivery, dispatch and goods receipt;
 * Service Procurement has no goods receipt and nothing is dispatched, and a
 * term that cannot be honoured is worse than no term because it is printed
 * and signed.
 *
 * Material Procurement is untouched: useTerms('payment_terms') still returns
 * every active row, so the purchase order keeps offering all forty-seven.
 */
export const useWorksPaymentTerms = () =>
  useQuery<Any>('payment_terms_works', () =>
    supabase.from('payment_terms').select('*')
      .eq('is_active', true).eq('works_applicable', true).order('sort_order'))

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
export const usePendingActions = (role?: string | null) =>
  useQuery<PendingAction>('pending_actions', () =>
    supabase.from('v_pending_actions').select('*').order('doc_date', { ascending: false }),
    [role], { cacheable: false, idField: 'doc_id' })

export const usePrs = (filter?: { site_id?: string; status?: string[]; pr_type?: string }) =>
  useQuery<PurchaseRequisition>('purchase_requisitions', () => {
    let q = supabase.from('purchase_requisitions').select('*')
    if (filter?.site_id) q = q.eq('site_id', filter.site_id)
    if (filter?.status?.length) q = q.in('status', filter.status)
    if (filter?.pr_type) q = q.eq('pr_type', filter.pr_type)
    return q.order('date_created', { ascending: false }).limit(500)
  }, [filter?.site_id, filter?.status?.join(','), filter?.pr_type])

export const usePos = (filter?: { site_id?: string; status?: string[] }) =>
  useQuery<PurchaseOrder>('purchase_orders', () => {
    let q = supabase.from('purchase_orders').select('*')
    if (filter?.site_id) q = q.eq('site_id', filter.site_id)
    if (filter?.status?.length) q = q.in('status', filter.status)
    return q.order('date_created', { ascending: false }).limit(500)
  }, [filter?.site_id, filter?.status?.join(',')])

export const useGrns = (filter?: { site_id?: string; status?: string[] }) =>
  useQuery<Grn>('grns', () => {
    let q = supabase.from('grns').select('*')
    if (filter?.site_id) q = q.eq('site_id', filter.site_id)
    if (filter?.status?.length) q = q.in('status', filter.status)
    return q.order('received_date', { ascending: false }).limit(500)
  }, [filter?.site_id, filter?.status?.join(',')])

export const useStock = (siteId?: string | null) =>
  useQuery<StockRow>('inventory_balances', () => {
    let q = supabase.from('v_stock_position').select('*')
    if (siteId) q = q.eq('site_id', siteId)
    return q.order('item_name')
  }, [siteId], { idField: 'material_id' })

export const useAudit = (docType: string, docId: string) =>
  useQuery<AuditEntry>(`audit_${docType}_${docId}`, () =>
    supabase.from('document_audit').select('*')
      .eq('doc_type', docType).eq('doc_id', docId)
      .order('created_at', { ascending: false }),
    [docType, docId], { cacheable: false })

/** One-off fetch used by detail screens that need lines alongside a header. */
export async function fetchOne<T>(table: string, id: string, select = '*'): Promise<T | null> {
  const { data, error } = await supabase.from(table).select(select).eq('id', id).maybeSingle()
  if (error) throw new Error(error.message)
  return (data as T) ?? null
}

export async function fetchLines<T>(table: string, fk: string, id: string, select = '*'): Promise<T[]> {
  const { data, error } = await supabase.from(table).select(select).eq(fk, id).order('sl_no')
  if (error) throw new Error(error.message)
  return (data ?? []) as T[]
}

/**
 * How much of an indent has already gone out of the site's own store.
 *
 * Returns a material_id -> quantity map, counting only APPROVED issue notes,
 * because an issue note awaiting the Head of Stores has not moved any stock and
 * must not be treated as settling part of the indent.
 *
 * Every screen that turns an indent into something else — the issue note and
 * the purchase requisition — has to net this off first, or the site ends up
 * asking twice for material it has already been given.
 */
export async function issuedAgainstIndent(indentId: string): Promise<Record<string, number>> {
  const { data: mins } = await supabase
    .from('material_issue_notes')
    .select('id').eq('source_indent_id', indentId).eq('status', 'APPROVED')

  const ids = (mins ?? []).map((m: { id: string }) => m.id)
  if (ids.length === 0) return {}

  const { data: lines } = await supabase
    .from('min_line_items')
    .select('material_id, quantity_issued').in('min_id', ids)

  const out: Record<string, number> = {}
  for (const l of (lines ?? []) as Array<{ material_id: string; quantity_issued: number }>) {
    out[l.material_id] = (out[l.material_id] ?? 0) + Number(l.quantity_issued || 0)
  }
  return out
}

/** Current stock for one material at one site — read from cache when offline. */
export async function stockOnHand(siteId: string, materialId: string): Promise<number> {
  if (navigator.onLine) {
    const { data } = await supabase
      .from('inventory_balances').select('quantity_on_hand')
      .eq('site_id', siteId).eq('material_id', materialId).maybeSingle()
    if (data) return Number(data.quantity_on_hand)
  }
  const cached = await readCache<StockRow>('inventory_balances')
  const row = cached.find((r) => r.site_id === siteId && r.material_id === materialId)
  return row ? Number(row.quantity_on_hand) : 0
}
